import { useMemo } from "react";
import { useIntl } from "react-intl";
import { Controller, get, useFormContext, useWatch } from "react-hook-form";

import { classNames } from "helpers/commonHelpers";
import testIds from "constants/testIds";
import FieldErrorBadge from "@RequestFormV2/components/Badges/FieldErrorBadge";
import { useReactiveReviewClear } from "@RequestFormV2/hooks/useReactiveReviewClear";
import FormSectionLabel from "pages/shipper/RequestFormV2/components/FormSectionLabel/FormSectionLabel";
import Radio from "components/v2/Radio/Radio";
import Textarea from "components/v2/Textarea/Textarea";
import Select from "components/v2/Select/Select";
import { useService } from "@RequestFormV2/sections/Service/context";

const INSURANCE_YES = "yes";
const INSURANCE_NO = "no";

function InsuranceField() {
  const intl = useIntl();
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();

  const insurance = useWatch({ control, name: "service.insurance" });
  const insuranceEnabled = insurance === true;
  const insuranceError = get(errors, "service.insurance");

  useReactiveReviewClear(
    "service.insurance",
    () => insurance === true || insurance === false,
    [insurance],
  );

  const radioOptions = useMemo(
    () => [
      {
        value: INSURANCE_YES,
        label: intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" }),
      },
      {
        value: INSURANCE_NO,
        label: intl.formatMessage({ id: "general.no", defaultMessage: "No" }),
      },
    ],
    [intl],
  );

  return (
    <div className="rfv2-two-column-grid__full-width">
      <label className={classNames({ required: true })}>
        {intl.formatMessage({
          id: "request-form.service.insurance.included.label",
          defaultMessage: "Insurance included",
        })}
      </label>
      <div className="width-fit">
        <Controller
          name="service.insurance"
          control={control}
          shouldUnregister
          rules={{}}
          render={({ field }) => (
            <Radio
              isInline
              options={radioOptions}
              value={field.value === true ? INSURANCE_YES : INSURANCE_NO}
              size="md"
              getOptionTestId={(option) =>
                option.value === INSURANCE_YES ? testIds.REQUEST_FORM_SERVICE_INSURANCE_CHECKBOX : undefined
              }
              onChange={(value) => {
                const isYes = value === INSURANCE_YES;
                field.onChange(isYes);
                if (!isYes) {
                  setValue("service.insurance_instructions", "");
                }
              }}
            />
          )}
        />
      </div>
      {insuranceError?.message && <FieldErrorBadge message={String(insuranceError.message)} />}
      {insuranceEnabled && (
        <div className="mt-md">
          <Textarea
            name="service.insurance_instructions"
            shouldUnregister
            label={intl.formatMessage({
              id: "new-request.service.insuranceInstructions",
              defaultMessage: "Insurance instructions",
            })}
            rows={2}
            testId="textarea-service-insurance_instructions"
          />
        </div>
      )}
    </div>
  );
}

export default function ServiceLevelSection() {
  const intl = useIntl();
  const { serviceDetailsConfig, serviceTypeOptions } = useService();

  const showIncotermFields = serviceDetailsConfig?.service_options?.incoterm !== "off";
  const incotermValues = serviceDetailsConfig?.service_options?.incoterm_values;
  const incotermIsRequired = serviceDetailsConfig?.service_options?.incoterm === "mandatory";
  const incotermDefault = serviceDetailsConfig?.service_options?.incoterm_default;

  const showInsurance = serviceDetailsConfig?.service_options?.ask_insurance !== "off";

  return (
    <div className="rfv2-form-section">
      <FormSectionLabel
        label={intl.formatMessage({ id: "request-form.service.serviceLevel.label", defaultMessage: "Service level" })}
      />
      <div className="rfv2-form-section__content">
        <div className="rfv2-two-column-grid">
          {serviceTypeOptions && serviceTypeOptions.length > 0 && (
            <>
              <Select
                label={intl.formatMessage({
                  id: "request-form.service.serviceRequirement.label",
                  defaultMessage: "Service requirement",
                })}
                name="service.service_type"
                required
                options={serviceTypeOptions}
                isClearable
                testId={testIds.REQUEST_FORM_SERVICE_REQUIREMENT}
              />
              <div />
            </>
          )}
          {showIncotermFields && (
            <>
              <Select
                label={intl.formatMessage({ id: "request-form.service.incoterms.label", defaultMessage: "Incoterms" })}
                name="service.incoterms"
                required={incotermIsRequired}
                defaultValue={incotermDefault}
                options={incotermValues}
                isClearable
                testId={testIds.REQUEST_FORM_SERVICE_INCOTERMS}
              />
              <div />
              <div className="rfv2-two-column-grid__full-width">
                <Textarea
                  label={intl.formatMessage({
                    id: "request-form.service.incoterms.instructions.label",
                    defaultMessage: "Incoterm instructions",
                  })}
                  name="service.incoterm_instructions"
                  testId="textarea-service-incoterm_instructions"
                />
              </div>
            </>
          )}
          {showInsurance && <InsuranceField />}
        </div>
      </div>
    </div>
  );
}
